import LogoutDialog from './logout-dialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';

import { siteConfig } from '@/configs/site-config';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import { Project, UserRole } from '@/state/use-admin';
import { Baseline } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function MainNav() {
	const pathname = window.location.pathname;
	const navigate = useNavigate();

	const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const [projects, setProjects] = useState<Project[]>([]);

	const { data: user, isLoading, error } = useUser();

	const getInitials = (name: string) => {
		return name
			.split(' ')
			.map(n => n[0])
			.join('')
			.toUpperCase();
	};

	const getRoleName = (role: UserRole) => {
		switch (role) {
			case UserRole.ADMIN:
				return '管理员';
			case UserRole.USER:
				return '用户';
			default:
				return '未知';
		}
	};

	useEffect(() => {
		setProjects(user?.projects || []);
	}, [user]);

	// 将重定向逻辑移到 useEffect 中
	useEffect(() => {
		if (error && !pathname.includes('/login')) {
			console.error(error);
			navigate('/login');
		}
	}, [error, pathname, navigate]);

	// 如果正在重定向或未认证，不渲染内容
	if (error) {
		return null;
	}

	if (isLoading) {
		return <div>Loading...</div>;
	}

	const handleLogout = () => {
		setIsDropdownOpen(false);
		setIsLogoutDialogOpen(true);
	};

	return (
		<div className="mr-4 flex w-full justify-between">
			<div className="flex">
				<Link to="/" className="mr-6 flex items-center space-x-2">
					<Baseline className="h-6 w-6" />
					<span className="font-bold inline-block">
						{siteConfig.name}
					</span>
				</Link>
				<nav className="flex items-center space-x-4 lg:space-x-6">

					{projects.map(project => {
						return (
							<Button
								key={project.id}
								asChild
								variant="ghost"
								className={cn(
									'transition-colors hover:text-foreground/80',
									pathname === '/dashboard'
										? 'text-foreground'
										: 'text-foreground/60'
								)}
							>
								<Link to={`/projects/${project.id}`}>
									{project.name}
								</Link>
							</Button>
						);
					})}

					<Button
						asChild
						variant="ghost"
						className={cn(
							'transition-colors hover:text-foreground/80',
							pathname === '/dashboard'
								? 'text-foreground'
								: 'text-foreground/60'
						)}
					>
						<Link to="/setting">设置</Link>
					</Button>
				</nav>
			</div>

			<DropdownMenu
				open={isDropdownOpen}
				onOpenChange={setIsDropdownOpen}
			>
				<DropdownMenuTrigger asChild>
					<Avatar className="h-8 w-8 cursor-pointer">
						<AvatarFallback>
							{user ? getInitials(user.name) : '?'}
						</AvatarFallback>
					</Avatar>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-40" align="end">
					<DropdownMenuLabel>我的账户</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem>
						<span className="font-medium">{user?.name}</span>
					</DropdownMenuItem>
					<DropdownMenuItem>
						<span className="text-muted-foreground">
							{user ? getRoleName(user.role) : '加载中...'}
						</span>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem onSelect={handleLogout}>
						登出
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			{/* <Button variant="ghost" onClick={() => setIsLogoutDialogOpen(true)}>
				<Label>登出</Label>
			</Button> */}

			<LogoutDialog
				isOpen={isLogoutDialogOpen}
				onClose={() => setIsLogoutDialogOpen(false)}
			/>
		</div>
	);
}
